import React, { useState, useEffect } from "react";
import { FaTimes, FaPlus, FaFolder, FaSpinner } from "react-icons/fa";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const EditLessonModal = ({ editId, editLessonModal, setEditLessonModal, fetchCourseContent }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [type, setType] = useState("video");
  const [contentFile, setContentFile] = useState(null);
  const [resources, setResources] = useState([]);
  const [isPreview, setIsPreview] = useState(false);
  const [fileKey, setFileKey] = useState(0);

  // Fetch lesson data
  useEffect(() => {
    const fetchLesson = async () => {
      if (!editId) return;
      try {
        const token = localStorage.getItem("access_token");
        const res = await fetch(`${BASE_URL}/courses/course/module/lesson/${editId}/`, {
          headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
        });
        if (!res.ok) throw new Error("Failed to fetch lesson");
        const data = await res.json();
        setTitle(data.title || "");
        setType(data.type || "video");
        setIsPreview(data.isPreview === 1); // assuming API returns 1/0
        // contentFile and resources cannot be pre-filled for file inputs
      } catch (err) {
        console.error(err);
        alert("Failed to fetch lesson data");
      }
    };
    fetchLesson();
  }, [editId]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) setEditLessonModal(false);
  };

  const getAcceptType = (type) => {
    switch (type) {
      case "video": return "video/*";
      case "audio": return "audio/*";
      case "pdf": return "application/pdf";
      case "doc": return ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      case "ppt": return ".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation";
      case "txt": return "text/plain";
      case "code": return ".js,.jsx,.ts,.tsx,.py,.java,.cpp,.c,.cs,.php,.rb,.go,.rs,.swift,.kt,.kts,.html,.css,.scss,.json,.xml,.yaml,.yml,.sql,.sh,.bash,.md";
      default: return "";
    }
  };

  const handleUpdate = async () => {
    if (!title || !editId) return;
    setIsLoading(true);

    const formData = new FormData();
    formData.append("title", title);
    formData.append("type", type);
    if (contentFile) formData.append("content_file", contentFile);
    resources.forEach((file) => formData.append("resources", file));
    formData.append("isPreview", isPreview ? "1" : "0");

    try {
      const res = await fetch(`${BASE_URL}/courses/course/module/lesson/${editId}/update/`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
        body: formData,
      });

      const data = await res.json();
      if (res.ok) {
        alert("Lesson updated successfully");
        fetchCourseContent();
        setEditLessonModal(false);
      } else {
        console.error("Error updating lesson:", data);
        alert("Failed to update lesson");
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  if (!editLessonModal) return null;

  return (
   <div
  className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 transition-all duration-300 ${
    editLessonModal ? "opacity-100 visible" : "opacity-0 invisible"
  }`}
  onClick={handleOverlayClick}
>
  <div
    className={`bg-white rounded-2xl mt-20 w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl border border-gray-100 transition-all duration-300 ${
      editLessonModal ? "translate-y-0 scale-100" : "-translate-y-4 scale-95"
    }`}
    onClick={(e) => e.stopPropagation()}
  >
    {/* Modal Header */}
    <div className="p-6 border-b border-gray-100">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
          <svg className="w-6 h-6 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">
            {isLoading ? "Updating Lesson..." : "Edit Lesson"}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {isLoading
              ? "Please wait while we update your lesson..."
              : "Edit your lesson details"}
          </p>
        </div>
        {!isLoading && (
          <button
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-red-500 hover:text-white transition-colors"
            onClick={() => setEditLessonModal(false)}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>

    {/* Modal Content */}
    <div className="p-6">
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-10 h-10 border-2 border-[#0f6abf] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-sm text-gray-500">Updating your lesson...</p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Lesson Title */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
              <label className="text-sm font-semibold text-gray-700">
                Lesson Title
              </label>
            </div>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
            />
          </div>

          {/* Type */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
              <label className="text-sm font-semibold text-gray-700">
                Type
              </label>
            </div>
            <select
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2390A3BF' stroke-width='1.5'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 16px center'
              }}
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setFileKey((prev) => prev + 1);
              }}
            >
              <option value="video">Video</option>
              <option value="audio">Audio</option>
              <option value="pdf">PDF</option>
              <option value="doc">DOC / DOCX</option>
              <option value="ppt">PPT / PPTX</option>
              <option value="txt">TXT</option>
              <option value="code">Code (JS, PY, HTML, etc.)</option>
            </select>
          </div>

          {/* Upload Content File */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
              <label className="text-sm font-semibold text-gray-700">
                Upload Content File
              </label>
            </div>
            <input
              key={fileKey}
              type="file"
              accept={getAcceptType(type)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-sm text-gray-900 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#e8f0f7] file:text-[#0f6abf] hover:file:bg-[#d0e0f0] transition-all"
              onChange={(e) => setContentFile(e.target.files[0])}
            />
          </div>

          {/* Upload Extra Resources */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
              <label className="text-sm font-semibold text-gray-700">
                Upload Extra Resources
              </label>
            </div>
            <input
              type="file"
              multiple
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-sm text-gray-900 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#e8f0f7] file:text-[#0f6abf] hover:file:bg-[#d0e0f0] transition-all"
              onChange={(e) => setResources([...e.target.files])}
            />
          </div>

          {/* Is Preview Checkbox */}
          <div className="flex items-center gap-3 mt-4">
            <div className="relative">
              <input
                type="checkbox"
                id="preview"
                className="sr-only"
                checked={isPreview}
                onChange={(e) => setIsPreview(e.target.checked)}
                disabled={!(type === "video" || type === "audio")}
              />
              <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                isPreview 
                  ? "border-[#0f6abf] bg-[#0f6abf]" 
                  : "border-gray-300 bg-white"
              }`}>
                {isPreview && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
            <label htmlFor="preview" className="text-sm text-gray-700 cursor-pointer">
              Is Preview?
            </label>
            {!(type === "video" || type === "audio") && (
              <span className="text-xs text-gray-500">(Only available for video/audio)</span>
            )}
          </div>
        </div>
      )}
    </div>

    {/* Modal Actions */}
    {!isLoading && (
      <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100">
        <div className="flex items-center justify-end gap-3">
          <button
            className="px-5 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
            onClick={() => setEditLessonModal(false)}
          >
            Cancel
          </button>
          <button
            className={`px-5 py-2.5 font-medium rounded-xl transition-all flex items-center gap-2 ${
              !title
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-[#0f6abf] text-white hover:bg-[#0a5ca8]"
            }`}
            onClick={handleUpdate}
            disabled={!title || isLoading}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Updating...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Update Lesson
              </>
            )}
          </button>
        </div>
      </div>
    )}
  </div>
</div>
  );
};

export default EditLessonModal;
